import { Lockup } from "./Lockup.js";
import { PortalFooter } from "./PortalShell.js";
import { SignInCard } from "./SignInCard.js";
import type { PortalBranding } from "./branding.js";
import { Check } from "./ui.js";

/**
 * The landing page — what `id.seplatgrowthacademy.com` is when nobody is signed in.
 *
 * It used to be a bare "Sign in" box on a grey page, which said nothing about what the address was
 * for. An applicant arrives here from a confirmation email months after applying, so the page has
 * to answer "what is this and why would I sign in" before it asks for an email address.
 *
 * The sign-in card sits inside the hero rather than below the copy: on a phone the hero collapses
 * to one column and the card is the second thing on the page, above the explanatory material,
 * because someone returning for the fourth time should not have to scroll past a pitch.
 */

const FEATURES: { title: string; body: string }[] = [
  {
    title: "Every application in one place",
    body: "Scholarship, Teachers Empowerment, Loco Parentis — every programme you have applied to, every year, with its reference number.",
  },
  {
    title: "Details you type once",
    body: "Your name, school, state of origin and bank details are carried into each new form, so a new application starts mostly filled in.",
  },
  {
    title: "Your data, your call",
    body: "Download everything we hold about you as a single file, or ask for it to be erased. Both are yours to do, without emailing anyone.",
  },
];

export const Landing = ({
  branding,
  onSignedIn,
}: {
  branding: PortalBranding;
  onSignedIn: (email: string) => void;
}): JSX.Element => (
  <div className="sga-page">
    <header className="sga-header">
      <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
        <Lockup branding={branding} height="h-8" />
        <span className="sga-muted hidden text-xs sm:inline">Applicant account</span>
      </div>
    </header>

    <main>
      <section className="sga-hero">
        <div className="mx-auto grid max-w-5xl gap-10 px-4 py-12 sm:px-6 lg:grid-cols-[1.05fr_minmax(340px,400px)] lg:items-center lg:py-16">
          <div>
            <p className="sga-eyebrow" style={{ color: "var(--sga-accent)" }}>
              Seplat Growth Academy
            </p>
            <h1 className="font-heading mt-3 text-3xl font-semibold leading-tight sm:text-4xl">
              One account for every programme, every year.
            </h1>
            <p className="sga-hero-lead mt-4 max-w-xl text-base sm:text-lg">
              Sign in to see the applications you have submitted, keep your details current so you
              never retype them, and download the data we hold about you.
            </p>

            {/* Hidden on a phone, where the whole point is to get the sign-in card near the top
                of the screen — the same four facts are in the card's own copy and in the feature
                cards below. */}
            <ul className="mt-6 hidden gap-2 text-sm sm:grid sm:grid-cols-2">
              {[
                "No password — we email you a code",
                "Works on any phone",
                "Reference numbers you can quote",
                "Free to use, always",
              ].map((point) => (
                <li key={point} className="sga-hero-lead flex items-start gap-2">
                  <Check className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>

          <SignInCard onSignedIn={onSignedIn} />
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
        <h2 className="font-heading text-xl font-semibold">What you can do here</h2>
        <div className="mt-5 grid gap-4 sm:grid-cols-3">
          {FEATURES.map((feature) => (
            <div key={feature.title} className="sga-card p-5">
              <h3 className="font-heading text-base font-semibold">{feature.title}</h3>
              <p className="sga-muted mt-2 text-sm">{feature.body}</p>
            </div>
          ))}
        </div>

        <div className="sga-card sga-tint mt-8 p-5">
          <h3 className="font-heading text-base font-semibold">Applying for the first time?</h3>
          <p className="sga-muted mt-2 max-w-3xl text-sm">
            This page is for people who have already applied. To start an application, open the link
            you were given for that programme — an account is created for you the first time you
            submit a form, using the email address you apply with.
          </p>
        </div>
      </section>
    </main>

    <PortalFooter branding={branding} />
  </div>
);
